/**
 * KeywordDedupWarning - Inline duplicate keyword warning.
 *
 * Placed below the keywords textarea. Debounces the primary keyword,
 * fires GET /blog-creation/content/check-keyword, and shows an amber
 * warning panel with match details if duplicates are found.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useKeywordDedupCheck } from '@/hooks/useContentPlannerAPI';
import { useDebounce } from '@/hooks/use-debounce';
import { universalBlogEditorPath } from '@/utils/editorRoutes';

interface KeywordDedupWarningProps {
  primaryKeyword: string;
  tenantId: string | null;
}

export const KeywordDedupWarning: React.FC<KeywordDedupWarningProps> = ({
  primaryKeyword,
  tenantId,
}) => {
  const debouncedKeyword = useDebounce(primaryKeyword, 800);

  const { data, isLoading, isFetching } = useKeywordDedupCheck(
    debouncedKeyword,
    tenantId,
  );

  if (!debouncedKeyword || debouncedKeyword.trim().length < 3) return null;

  if (isLoading || isFetching) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
        <RefreshCw className="h-3 w-3 animate-spin" />
        Checking for existing content...
      </div>
    );
  }

  if (!data?.exists) return null;

  return (
    <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-2">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
            Content already exists for this keyword
          </p>
          <div className="mt-1.5 space-y-1">
            {data.matches.map((match) => (
              <div key={match.id} className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400">
                <Link
                  to={universalBlogEditorPath(match.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex min-w-0 items-center gap-1 truncate font-medium text-amber-800 dark:text-amber-300 underline decoration-amber-300 underline-offset-2 hover:text-amber-900 hover:decoration-amber-500"
                  title="Open the existing article in a new tab"
                >
                  <span className="truncate">{match.title || match.keyword}</span>
                  <ExternalLink className="h-3 w-3 shrink-0 opacity-60 group-hover:opacity-100" />
                </Link>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                  {match.status}
                </Badge>
              </div>
            ))}
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
            You can still create content &mdash; this is just a heads up.
          </p>
        </div>
      </div>
    </div>
  );
};
